import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

/**
 * The life of a floating panel: a menu, a dialog, the search suggestions.
 *
 * This is the single home for open / outside-press / Escape on the whole site.
 * Before it there were four hand copies, on three different event names, only
 * one of them using the capture phase, only one stopping Escape from reaching
 * whatever was behind it, and none of them returning focus to the trigger. Two
 * of the four live in `@majourneys/discover`, which cannot import from the app,
 * so the shared home had to be a package.
 *
 * Moved here verbatim from `apps/majourneys/src/shared/ui/use-presence.ts`,
 * which now re-exports these so the header's five menus keep working unchanged.
 *
 * Every `window` and `document` touch is inside an effect: `@majourneys/discover`
 * renders on the server.
 */

/** How long the pop transition takes to leave. */
export const POP_MS = 200;

/**
 * Keeps a panel mounted through its exit.
 *
 * A React unmount is instant, so the closed frame has to be painted first:
 * mount, force a style flush, then flip to open. On close flip to closed and
 * unmount once the transition has run.
 */
export function usePresence(open: boolean, ms: number = POP_MS) {
  const [mounted, setMounted] = useState(open);
  const [state, setState] = useState<"open" | "closed">(open ? "open" : "closed");
  const node = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setState("closed");
    const t = window.setTimeout(() => setMounted(false), ms);
    return () => window.clearTimeout(t);
  }, [open, ms]);

  // After the closed frame is in the DOM, force a style flush, then open.
  useLayoutEffect(() => {
    if (!open || !mounted) return;
    void node.current?.offsetWidth;
    setState("open");
  }, [open, mounted]);

  const ref = useCallback((el: HTMLElement | null) => {
    node.current = el;
  }, []);

  return { mounted, state, ref };
}

/**
 * The open state of a set of menus, keyed by name, so two of them cannot be
 * open at once. Owned by the bar and handed to each menu; a menu rendered on
 * its own gets a private group from `useMenu`.
 */
export type MenuGroup = {
  openName: string | null;
  setOpenName: (next: string | null) => void;
  /** True when the open menu replaced another: the swap skips the grow-in. */
  instant: boolean;
  /**
   * Every member's box. A press outside the open menu closes it — unless it
   * lands on another member, whose own toggle then takes over. Without this
   * the second menu would always open from "nothing open" and never swap.
   */
  boxes: Set<HTMLElement>;
};

export function useMenuGroup(): MenuGroup {
  const [openName, setOpen] = useState<string | null>(null);
  const [instant, setInstant] = useState(false);
  const current = useRef<string | null>(null);
  const boxes = useRef(new Set<HTMLElement>()).current;

  const setOpenName = useCallback((next: string | null) => {
    const prev = current.current;
    current.current = next;
    setInstant(prev !== null && next !== null && prev !== next);
    setOpen(next);
  }, []);

  return useMemo(
    () => ({ openName, setOpenName, instant, boxes }),
    [openName, setOpenName, instant, boxes],
  );
}

export interface UseMenuOptions {
  /**
   * A second surface that counts as inside the menu.
   *
   * The phone body is a bottom sheet portalled to `<body>`, because the sheet
   * it opens from carries a `transform`, and a transform makes its element the
   * containing block for anything `position: fixed` inside it. Portalled, the
   * sheet is no longer a DOM descendant of the menu's box, so without this a
   * press on one of its own rows would read as a press outside.
   */
  readonly surfaceRef?: RefObject<HTMLElement | null>;
}

/**
 * One menu in a group. `ref` goes on the box that holds the trigger and the
 * panel; while open, a pointerdown outside it or Escape closes it.
 *
 * Both listeners run in the capture phase, so Escape never reaches whatever is
 * behind the menu. That is not a nicety: inside the Discover sheet and inside
 * the account modal, one Escape used to close the menu *and* the thing under
 * it.
 */
export function useMenu<T extends HTMLElement = HTMLDivElement>(
  group: MenuGroup | undefined,
  name: string,
  options?: UseMenuOptions,
) {
  const local = useMenuGroup();
  const g = group ?? local;
  const { setOpenName, boxes } = g;
  const open = g.openName === name;
  const ref = useRef<T>(null);
  const surfaceRef = options?.surfaceRef;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    boxes.add(el);
    return () => {
      boxes.delete(el);
    };
  }, [boxes]);

  useEffect(() => {
    if (!open) return;
    if (typeof document === "undefined") return;
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      const own = ref.current;
      if (own?.contains(target)) return;
      if (surfaceRef?.current?.contains(target)) return;
      for (const box of boxes) if (box !== own && box.contains(target)) return;
      setOpenName(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpenName(null);
    };
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, setOpenName, boxes, surfaceRef]);

  const setOpen = useCallback(
    (next: boolean) => setOpenName(next ? name : null),
    [setOpenName, name],
  );
  const close = useCallback(() => setOpenName(null), [setOpenName]);
  const toggle = useCallback(() => setOpenName(open ? null : name), [setOpenName, open, name]);

  return { open, setOpen, close, toggle, ref, instant: g.instant };
}

/** A menu grows out of its trigger: the corner it hangs from, in either direction. */
export function popOrigin(side: "start" | "end", dir: string): string {
  const rtl = dir === "rtl";
  return (side === "end") !== rtl ? "top right" : "top left";
}
